import React, { useEffect, useState, useCallback } from 'react';
import { History, RefreshCw } from 'lucide-react';
import apiClient from '../utils/apiClient';
import { useFacility } from '../context/FacilityContext';
import { formatDateTime } from '../utils/datetime';

/**
 * CustomerHistory — narrative + raw timelines for one customer.
 *
 * Sibling component to UnitHistory. Designed to render either at a
 * standalone route OR (preferred) embedded inside CustomerForm.js as a
 * tab. The wrapper page (route or tab) supplies `customerId`.
 *
 * Pill toggle at the top:
 *   Curated  ->  story timeline (rentals + invoices + payments + notes
 *                + meaningful audit edits, status flips collapsed)
 *   Raw      ->  every synthesized fact row + audit_logs + *_events
 *                row, straight three-column dump.
 *
 * Preference persists in localStorage["customer_history_view"] (DELIBERATELY
 * separate from the unit_history_view key so each surface keeps its own
 * default).
 */

const ACTION_COLOR = {
  rental_started: 'text-blue-700 bg-blue-50 border-blue-200',
  rental_ended: 'text-rose-700 bg-rose-50 border-rose-200',
  invoice_created: 'text-violet-700 bg-violet-50 border-violet-200',
  payment_received: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  payment_applied: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  note_added: 'text-amber-700 bg-amber-50 border-amber-200',
  // unit/rental event types still in play (richer detail when present):
  created: 'text-blue-700 bg-blue-50 border-blue-200',
  occupied: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  vacated: 'text-amber-700 bg-amber-50 border-amber-200',
  rate_changed: 'text-violet-700 bg-violet-50 border-violet-200',
  status_changed: 'text-slate-700 bg-slate-100 border-slate-200',
  lease_executed: 'text-blue-700 bg-blue-50 border-blue-200',
  ended: 'text-rose-700 bg-rose-50 border-rose-200',
  late_fee_applied: 'text-rose-700 bg-rose-50 border-rose-200',
  customer_edited: 'text-slate-700 bg-slate-100 border-slate-200',
  unit_edited: 'text-slate-700 bg-slate-100 border-slate-200',
};

const STORAGE_KEY = 'customer_history_view';

const prettyAction = (a) => {
  if (!a) return 'Event';
  return a.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatValue = (v) => {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'object') {
    try { return JSON.stringify(v); } catch { return String(v); }
  }
  return String(v);
};

const CustomerHistory = ({ customerId }) => {
  const { currentFacility, facilityId } = useFacility();
  const timeZone = currentFacility?.timezone || undefined;

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [mode, setMode] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || 'curated'; } catch { return 'curated'; }
  });

  const changeMode = (next) => {
    setMode(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
  };

  const load = useCallback(async () => {
    const fid = facilityId || currentFacility?._id || currentFacility?.id;
    if (!fid || !customerId) return;
    setLoading(true);
    setError(null);
    try {
      const h = await apiClient.get(
        `/api/facilities/${fid}/customers/${customerId}/history?mode=${mode}&limit=300`
      );
      setEvents(h.data?.events || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [facilityId, currentFacility, customerId, mode]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="mb-4 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-[15px] font-semibold text-slate-900 flex items-center gap-2">
            <History className="h-4 w-4 text-violet-600" />
            Timeline
          </h2>
          <p className="text-[12px] text-slate-400 mt-0.5">
            Every rental, invoice, payment, note, and edit tied to this customer.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 hover:border-slate-300 rounded text-[12px] text-slate-700"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <div className="mb-4 inline-flex rounded-full border border-slate-200 bg-white p-0.5">
        {['curated', 'raw'].map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => changeMode(m)}
            className={`px-4 py-1.5 rounded-full text-[12px] font-medium transition-colors capitalize ${
              mode === m ? 'bg-blue-600 text-white' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            {m}
          </button>
        ))}
      </div>

      {error && (
        <div className="rounded-xl border border-rose-200/80 bg-rose-50/50 p-3 mb-4">
          <p className="text-[12px] text-rose-700">{error}</p>
        </div>
      )}

      {mode === 'curated' ? (
        <CuratedView events={events} loading={loading} timeZone={timeZone} />
      ) : (
        <RawView events={events} loading={loading} timeZone={timeZone} />
      )}
    </div>
  );
};

const CuratedView = ({ events, loading, timeZone }) => (
  <div className="rounded-xl border border-slate-200/80 bg-white p-4">
    {loading && events.length === 0 ? (
      <p className="text-[13px] text-slate-400">Loading…</p>
    ) : events.length === 0 ? (
      <p className="text-[13px] text-slate-400">No history yet for this customer.</p>
    ) : (
      <ol className="space-y-3">
        {events.map((e, idx) => {
          const cls = ACTION_COLOR[e.action_type] || 'text-slate-700 bg-slate-100 border-slate-200';
          return (
            <li key={e.raw_id || idx} className="flex items-start gap-3">
              <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium border whitespace-nowrap ${cls}`}>
                {prettyAction(e.action_type)}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] text-slate-800 break-words">{e.summary}</div>
                <div className="text-[11px] text-slate-400 mt-0.5">
                  {formatDateTime(e.timestamp, timeZone)}{e.actor ? ` · ${e.actor}` : ''}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    )}
  </div>
);

const RawView = ({ events, loading, timeZone }) => (
  <div className="rounded-xl border border-slate-200/80 bg-white overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full text-[12px]">
      <thead className="bg-slate-50 border-b border-slate-200">
        <tr>
          <th className="text-left px-4 py-2 font-semibold text-slate-600 w-44">Date</th>
          <th className="text-left px-4 py-2 font-semibold text-slate-600 w-44">Action</th>
          <th className="text-left px-4 py-2 font-semibold text-slate-600">Details</th>
        </tr>
      </thead>
      <tbody>
        {loading && events.length === 0 ? (
          <tr>
            <td colSpan={3} className="px-4 py-4 text-slate-400">Loading…</td>
          </tr>
        ) : events.length === 0 ? (
          <tr>
            <td colSpan={3} className="px-4 py-4 text-slate-400">No history yet for this customer.</td>
          </tr>
        ) : (
          events.map((e, idx) => (
            <tr key={e.raw_id || idx} className="border-b border-slate-100 last:border-0 align-top">
              <td className="px-4 py-2 text-slate-600 whitespace-nowrap">
                {formatDateTime(e.timestamp, timeZone)}
                {e.actor && (
                  <div className="text-[11px] text-slate-400">{e.actor}</div>
                )}
              </td>
              <td className="px-4 py-2 text-slate-700 whitespace-nowrap">
                <span className="font-medium">{prettyAction(e.action_type)}</span>
                <div className="text-[11px] text-slate-400">{e.source}</div>
              </td>
              <td className="px-4 py-2 text-slate-700 break-all">
                <div>{e.summary}</div>
                {e.details && Object.keys(e.details).length > 0 && (
                  <div className="mt-1 text-[11px] text-slate-500 font-mono">
                    {Object.entries(e.details).map(([k, v]) => (
                      <span key={k} className="mr-3">
                        <span className="text-slate-400">{k}:</span> {formatValue(v)}
                      </span>
                    ))}
                  </div>
                )}
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
    </div>
  </div>
);

export default CustomerHistory;
